import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getServicePagePath } from "../../../../services/navigation/navigateToRoute";
import type { AppRouterPaths } from "../../../../services/navigation/types";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { setSelectedObjectId } from "../slice";
import { useStorageSelectors } from "./use-storage-selectors";
import { StorageScope } from "../../../../services/api/instances/storageApi/types/service_types";
import { LAST_SELECTED_KEY } from "../constants";
import { LastSelectedEntry } from "../types";

export function useServiceNavigate(
  service: StorageScope,
  pathName: keyof AppRouterPaths,
) {
  const dispatch = useAppDispatch();
  const { objects, selectedId } = useStorageSelectors(service);
  const navigate = useNavigate();

  const hasInitialized = useRef(false);
  const objectsRef = useRef(objects);

  useEffect(() => {
    objectsRef.current = objects;
  }, [objects]);

  useEffect(() => {
    hasInitialized.current = false;
  }, [service]);

  useEffect(() => {
    if (selectedId) {
      navigate(getServicePagePath(selectedId, pathName));
    }
  }, [selectedId]);

  useEffect(() => {
    if (!objects?.length) return;

    if (!hasInitialized.current) {
      hasInitialized.current = true;
      const lastId = getLastSelectedId();
      const finalId =
        objects.find((obj) => obj.id === lastId)?.id ?? objects[0].id;
      dispatch(setSelectedObjectId({ service, id: finalId }));
      navigate(getServicePagePath(lastId, pathName));
    }
  }, [objects, service, pathName, dispatch, navigate]);

  const getLastSelectedId = useCallback((): string | null => {
    try {
      const raw = localStorage.getItem(LAST_SELECTED_KEY);
      if (!raw) {
        if (objects) {
          return objectsRef.current?.[0]?.id ?? null;
        }
        return null;
      }

      const data: Record<string, LastSelectedEntry> = JSON.parse(raw);
      return data[service]?.id || null;
    } catch (e) {
      console.warn("Failed to read last selected object from localStorage", e);
      return null;
    }
  }, [service]);

  return {
    getLastSelectedId,
  };
}
